/**
 * sessionCalibrationCriteria — which criteria the S4 scale-calibration panel
 * puts in front of a facilitator, and on which scale.
 *
 * The panel used to derive its rows from the whole attributes store, filtered
 * to "has a scale". That store seeds itself with client-side defaults
 * (`impact` / `effort` / `urgency` carry hardcoded scales) and merges them with
 * whatever the workspace actually defined — so a workspace whose real criteria
 * are `confidence` / `reach` / … (none with `metadata.scales`) was offered
 * Impact / Effort / Urgency to calibrate and none of its own. Any calibration
 * written from that list keyed a governance row to a criterion the session
 * never scored (#619).
 *
 * The source of truth is the session's own criteria, resolved the same way the
 * scoring tools resolve them — `criteria_config` → `v3Config.criteria`
 * (`resolveSessionScoringCriteria`). Every one of those gets a row; a
 * criterion whose workspace attribute row carries a scale is calibrated on
 * that scale, anything else on the 1–5 numeric scale the scoring tools use.
 * Client-default attributes (no DB `id`) never contribute a scale: a session
 * that declared `impact` in a workspace with no `impact` row is calibrating a
 * session-authored criterion, not the product's sample one.
 */

import type { Attribute, MeasurementScale } from '../types/prioritization/lifecycle';
import {
  resolveSessionScoringCriteria,
  type SessionCriteriaCarrier,
} from './sessionScoringCriteria';

/**
 * The 1–5 numeric scale the scoring tools use (`ScoringScale { min: 1, max: 5 }`
 * in simple-weighted-scoring). Same id/label as the attributes store's
 * `DEFAULT_SCALE_1_5` so a calibration keyed on it matches the attribute's own
 * scale if the workspace later adopts one.
 */
export const CALIBRATION_FALLBACK_SCALE: MeasurementScale = {
  id: 'default-1-5',
  label: '1–5 Numeric',
  scaleType: 'numeric',
  min: 1,
  max: 5,
  step: 1,
  points: [1, 2, 3, 4, 5].map((v) => ({ value: v, label: String(v) })),
  isDefault: false,
};

export interface CalibrationCriterion {
  /** The criterion id as the session declares it — an attribute key or a `crit_*` id. */
  key: string;
  label: string;
  definition?: string;
  scale: MeasurementScale;
}

function scaleForAttribute(attr: Attribute | undefined): MeasurementScale | undefined {
  // Only an attribute the workspace actually holds (DB row → `id`) may lend
  // its scale; the merged client defaults have none.
  if (!attr?.id || !attr.scales || attr.scales.length === 0) return undefined;
  return attr.scales.find((s) => s.id === attr.defaultScaleId) ?? attr.scales[0];
}

/**
 * The rows the calibration panel should show for `session`, in the session's
 * own criterion order. Empty when the session has declared no criteria
 * anywhere — the panel must not fall back to tool or client defaults, because
 * what it writes is keyed by `attribute_key`.
 */
export function criteriaToCalibrate(
  session: SessionCriteriaCarrier | null | undefined,
  attributes: readonly Attribute[],
): CalibrationCriterion[] {
  const byKey = new Map(attributes.map((a) => [a.key, a] as const));
  const resolved = resolveSessionScoringCriteria(session, [], (id) => byKey.get(id));
  if (resolved.source === 'tool_default') return [];

  return resolved.criteria.map((criterion) => {
    const attr = byKey.get(criterion.id);
    return {
      key: criterion.id,
      label: criterion.name,
      definition: attr?.id ? attr.definition : criterion.description || undefined,
      scale: scaleForAttribute(attr) ?? CALIBRATION_FALLBACK_SCALE,
    };
  });
}
